import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Avatar,
  Container,
  Paper,
} from "@mui/material";
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";
import { ResetPasswordRequest } from "../../services/usersService";
import { NotificationManager } from "react-notifications";

const ResetPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success', 'error', or null
  const [message, setMessage] = useState("");
  const [emailError, setEmailError] = useState("");

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    // Clear previous errors
    setEmailError("");
    setStatus(null);
    setMessage("");

    // Validate email if not empty
    if (value && !validateEmail(value)) {
      setEmailError("Please enter a valid email address");
    }
  };

  const handleResetPassword = async () => {
    // Validate email before submission
    if (!email) {
      setEmailError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    setStatus(null);
    setMessage("");

    try {
      // NotificationManager.warning("Reset password coming soon.");
      // return
      let response = await ResetPasswordRequest(email);

      NotificationManager.success("Logged in successfully.");

      setStatus("success");
      setMessage(
        "Password reset link has been sent to your email address. Please check your inbox and follow the instructions."
      );
      setEmail(""); // Clear form on success
    } catch (error) {      
       NotificationManager.error("Error", "Error occurred while sending password reset link." );
      console.error("Error occurred while sending password reset link");
      setStatus("error");
      setMessage(
        "Failed to send reset link. Please try again or contact support if the problem persists."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    // Navigate back or reload - replace with proper navigation
    window.location.reload();
  };

  const resetForm = () => {
    setEmail("");
    setEmailError("");
    setStatus(null);
    setMessage("");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #e3f2fd 0%, #ffffff 50%, #f3e5f5 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        {/* Header Section */}
        <Box textAlign="center" mb={4}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: "primary.main",
              mb: 2,
              mx: "auto",
              background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
            }}
          >
            <EmailIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography
            variant="h4"
            component="h1"
            fontWeight="bold"
            gutterBottom
          >
            Reset Password
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enter your email address and we'll send you a link to reset your
            password.
          </Typography>
        </Box>

        {/* Main Card */}
        <Paper
          elevation={8}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {status === "success" ? (
              /* Success State */
              <Box textAlign="center">
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: "success.light",
                    mb: 3,
                    mx: "auto",
                  }}
                >
                  <CheckCircleIcon
                    sx={{ fontSize: 32, color: "success.main" }}
                  />
                </Avatar>
                <Typography variant="h6" fontWeight="semibold" gutterBottom>
                  Check Your Email
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3, lineHeight: 1.6 }}
                >
                  {message}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={resetForm}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: "semibold",
                      boxShadow: 2,
                      "&:hover": {
                        boxShadow: 4,
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    Send Another Link
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleGoBack}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: "semibold",
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </Box>
            ) : (
              /* Form State */
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Error Alert */}
                {status === "error" && (
                  <Alert
                    severity="error"
                    icon={<ErrorOutlineIcon />}
                    sx={{
                      borderRadius: 2,
                      "& .MuiAlert-message": {
                        lineHeight: 1.6,
                      },
                    }}
                  >
                    {message}
                  </Alert>
                )}

                {/* Email Input */}
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  error={!!emailError}
                  helperText={emailError}
                  disabled={isLoading}
                  placeholder="Enter your email address"
                  InputProps={{
                    startAdornment: (
                      <EmailIcon
                        sx={{
                          color: emailError ? "error.main" : "action.active",
                          mr: 1,
                        }}
                      />
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderWidth: 2,
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderWidth: 2,
                        },
                      },
                    },
                  }}
                />

                {/* Action Buttons */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleResetPassword}
                    disabled={isLoading || !!emailError || !email}
                    startIcon={
                      isLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: "semibold",
                      boxShadow: 2,
                      "&:hover": {
                        boxShadow: 4,
                        transform: "translateY(-1px)",
                      },
                      "&:disabled": {
                        transform: "none",
                      },
                    }}
                  >
                    {isLoading ? "Sending Link..." : "Send Reset Link"}
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleGoBack}
                    disabled={isLoading}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: "semibold",
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Paper>

        {/* Footer */}
        <Box textAlign="center" mt={4}>
          <Typography variant="body2" color="text.secondary">
            Didn't receive the email? Check your spam folder or{" "}
            <Button
              variant="text"
              size="small"
              onClick={resetForm}
              sx={{
                textTransform: "none",
                textDecoration: "underline",
                fontWeight: "semibold",
                p: 0,
                minWidth: "auto",
              }}
            >
              try again
            </Button>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ResetPassword;
